import FormControl from "common/components/FormControl/FormControl";
import FormScrollError from "common/components/FormScrollError/FormScrollError";
import { useCallback, useMemo, useState } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import arrayMutators from "final-form-arrays";
import EditHeader from "common/components/EditHeader/EditHeader";
import useRouter from "common/hooks/use-router";
import HeaderButtonsEdit from "common/components/EditHeader/HeaderButtonsEdit";
import { FieldArray } from "react-final-form-arrays";
import SortableList from "common/components/SortableList/SortableList";
import FormAddButton from "common/components/FormAddButton.tsx/FormAddButton";
import { BiMoveVertical } from "react-icons/bi";
import Button from "common/components/Button/Button";
import usePark from "common/hooks/usePark";
import useAuth from "common/hooks/useAuth";
import SpecySelect from "components/select/SpecySelect/SpecySelect";

function DragHandle(props: any) {
  return (
    <BiMoveVertical
      {...props}
      className="w-4 h-4 cursor-move hover:text-primary border-none"
    />
  );
}

function StepItem(form: any, values: any, rest: any, isEnabled: boolean) {
  const { t } = useTranslation();

  return (
    <div
      key={rest.name}
      className="relative mt-2 flex gap-3 text-sm items-start justify-between w-full rounded-lg border border-green-900/10 py-2 pl-3 pr-1 bg-green-900/5"
    >
      <div
        className="min-h-[3rem] w-12 p-0 flex justify-center items-center cursor-pointer hover:text-primary bg-white rounded-sm border-slate-200"
        {...rest.dragProps}
      >
        <DragHandle />
      </div>

      <div className="flex flex-col w-full gap-3">
        <div className="flex flex-row w-full gap-2">
          <div className="text-xs w-full">
            <FormControl
              name={`${rest.name}.name`}
              title={t("forms.stepName")}
              containerStyle="w-full"
              required
              editable={isEnabled}
            />
          </div>
        </div>
      </div>

      {rest.index > 0 && (
        <div className="">
          <Button
            type="error-line"
            onClick={() => rest.fields.remove(rest.index)}
            icon="TrashIcon"
            className="min-h-[3rem] w-12 p-0 flex justify-center"
            compact
            disabled={!isEnabled}
          />
        </div>
      )}
    </div>
  );
}

function TrainingExerciseForm({
  onSubmit,
  trainingExercise,
  isFromImport = false,
}: any) {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const [errorMessage, setErrorMessage] = useState("");
  const { park: currentPark } = usePark();
  const { user: currentUser } = useAuth();
  const canManageTrainingExercise = trainingExercise?.id
    ? (currentUser?.isAdmin ||
        currentUser?.isSuperadmin ||
        ((currentUser?.isManager || currentUser?.isObserver) &&
          currentUser?.administrationPermissions?.["training-grid"]?.update)) ??
      false
    : (currentUser?.isAdmin ||
        currentUser?.isSuperadmin ||
        ((currentUser?.isManager || currentUser?.isObserver) &&
          currentUser?.administrationPermissions?.["training-grid"]?.create)) ??
      false;

  const initialValues = useMemo(
    () =>
      trainingExercise?.id || isFromImport
        ? {
            ...trainingExercise,
            park: trainingExercise.park?.id ?? currentPark?.id,
            species: trainingExercise.species?.map((sp: any) => sp.id),
          }
        : {
            name: "",
            park: currentPark?.id,
            steps: [
              {
                name: "",
              },
            ],
          },
    [currentPark?.id, isFromImport, trainingExercise]
  );

  const onSubmitWrapper = async (values: any) => {
    const res = await onSubmit(values);

    if (res.message) {
      setErrorMessage(res.message);
    }
  };

  const handleCancel = useCallback(() => {
    if (isFromImport) {
      navigate(-1);
    } else {
      navigate("/training-exercises", { replace: true });
    }
  }, [isFromImport, navigate]);

  return (
    <Form
      onSubmit={onSubmitWrapper}
      mutators={{
        ...arrayMutators,
        setFieldValue: ([field]: any, state: any, utils: any) => {
          utils.changeValue(state, field.field, () => field.value);
        },
      }}
      subscription={{ values: true, submitting: true, pristine: true }}
      initialValues={initialValues}
      render={({ handleSubmit, values, form }) => {
        return (
          <div className="mx-auto max-w-full py-4 px-4 text-slate-500 text-base">
            {/* Header */}
            <EditHeader
              title={t(
                trainingExercise?.id
                  ? "editItem.trainingExercise"
                  : "newItem.trainingExercise"
              )}
              subtitle={values.name}
              icon="FiBox"
              errorMessage={errorMessage}
              HeaderButtonsEdit={
                <HeaderButtonsEdit
                  inHeader
                  onSubmit={
                    canManageTrainingExercise ? handleSubmit : undefined
                  }
                  onCancel={handleCancel}
                />
              }
            />
            <form onSubmit={handleSubmit} noValidate>
              <FormScrollError />

              <div className="responsive-flex gap-2 mt-2">
                <div className="md:w-full mb-2 md:mb-0">
                  <div className="flex flex-col gap-2 w-full">
                    <div className="white-box">
                      <div className="responsive-flex gap-4">
                        <div className="flex flex-col w-full">
                          <div className="flex flex-row gap-4">
                            <FormControl
                              name="name"
                              title={t("forms.exerciseName")}
                              required
                              containerStyle="flex w-full mb-4"
                              editable={canManageTrainingExercise}
                              className="text-input"
                            />
                            <FormControl
                              name="species"
                              title={t("forms.species")}
                              editable={canManageTrainingExercise}
                              type="custom"
                              multiple
                              containerStyle="flex w-full mb-4"
                            >
                              <SpecySelect />
                            </FormControl>
                          </div>
                          <div className="flex flex-row gap-4">
                            <FormControl
                              name="desiredBehavior"
                              title={t("forms.desiredBehavior")}
                              type="textarea"
                              containerStyle="flex w-full mb-4"
                              editable={canManageTrainingExercise}
                              className="text-input"
                            />
                            <FormControl
                              name="behavioralGoals"
                              title={t("forms.behavioralGoals")}
                              type="textarea"
                              containerStyle="flex w-full mb-4"
                              editable={canManageTrainingExercise}
                              className="text-input"
                            />
                          </div>
                          <div className="flex flex-row gap-4">
                            <FormControl
                              name="discriminativeStimulus"
                              title={t("forms.discriminativeStimulus")}
                              type="textarea"
                              containerStyle="flex w-full mb-4"
                              editable={canManageTrainingExercise}
                              className="text-input"
                            />
                            <FormControl
                              name="reinforcers"
                              title={t("forms.reinforcers")}
                              type="textarea"
                              containerStyle="flex w-full mb-4"
                              editable={canManageTrainingExercise}
                              className="text-input"
                            />
                          </div>
                          <div className="flex flex-row gap-4">
                            <FormControl
                              name="materials"
                              title={t("forms.materials")}
                              type="textarea"
                              containerStyle="flex w-full mb-4"
                              editable={canManageTrainingExercise}
                              className="text-input"
                            />
                            <FormControl
                              name="security"
                              title={t("forms.security")}
                              type="textarea"
                              containerStyle="flex w-full mb-4"
                              editable={canManageTrainingExercise}
                              className="text-input"
                            />
                          </div>
                          <FieldArray name="steps">
                            {({ fields }: any) => (
                              <div>
                                <SortableList
                                  items={fields}
                                  onMove={fields.move}
                                  render={(rest) =>
                                    StepItem(
                                      form,
                                      values,
                                      rest,
                                      canManageTrainingExercise ?? false
                                    )
                                  }
                                />

                                {canManageTrainingExercise && (
                                  <FormAddButton
                                    label={t("newItem.step")}
                                    onClick={() =>
                                      fields.push({
                                        id: Math.floor(Math.random() * 10000),
                                      })
                                    }
                                  />
                                )}
                              </div>
                            )}
                          </FieldArray>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <HeaderButtonsEdit
                onSubmit={handleSubmit}
                onCancel={() => navigate("/training-exercises")}
              />
            </form>
          </div>
        );
      }}
    />
  );
}
export default TrainingExerciseForm;
